import React, { useEffect } from 'react';
import { PrimaryButton } from '@workday/canvas-kit-react/button';
import { FormField } from '@workday/canvas-kit-react/form-field';
import { TextInput } from '@workday/canvas-kit-react/text-input';
import { useForm } from 'react-hook-form';
import { autoId, CardView, emailRegex, formFieldErrorPropsV2 } from '@workday/upc-common';
import { ID_CREATE_ACCOUNT_BUTTON, ID_EMAIL, ID_LOGIN_BUTTON, ID_UNAUTHENTICATED } from '../../../autoIds';
import styles from './LoginEmailEntry.module.scss';
import { CREATE_ACCOUNT_PATH, PASSWORD_PATH } from '../../../react-router-paths';
import { useInitEmailLoginMutation } from '../../../slices/auth-api.slice';
import { useSelector } from 'react-redux';
import { selectUserEmail } from '../../../slices/login.slice';
import { ACCOUNT_DEFAULT_USER } from '../../../nx-environment-variables';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TertiaryButtonLink } from '../../reset-password/tertiary-button-link';

interface LoginEmailForm {
    email: string;
}

export function LoginEmailEntry() {
    const userEmail = useSelector(selectUserEmail);

    const { t } = useTranslation('auth');

    const [initEmailLogin, { isLoading, isSuccess }] = useInitEmailLoginMutation();

    const { formState, handleSubmit, register } = useForm<LoginEmailForm>({
        defaultValues: {
            email: userEmail || ACCOUNT_DEFAULT_USER || ''
        },
        reValidateMode: 'onSubmit'
    });

    const history = useHistory();

    useEffect(() => {
        if (isSuccess) {
            history.push(PASSWORD_PATH);
        }
    }, [history, isSuccess]);

    function onSubmit(values) {
        initEmailLogin({ email: values.email });
    }

    const disabled = isLoading;

    return (
        <div {...autoId(ID_UNAUTHENTICATED)} className={styles.loginEmailEntry}>
            <h1 className={styles.title}>{t('LoginEmailEntry.title')}</h1>
            <h2 className={styles.subTitle}>{t('LoginEmailEntry.subTitle')}</h2>
            <CardView className={styles.card}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <FormField
                        className={styles.email}
                        grow={true}
                        inputId={ID_EMAIL}
                        label={t('LoginEmailEntry.emailAddressLabel')}
                        {...formFieldErrorPropsV2(
                            formState.errors.email,
                            undefined,
                            t('Required.fieldErrorLabel', { ns: 'shared' })
                        )}>
                        <TextInput
                            {...autoId(ID_EMAIL)}
                            autoComplete='username'
                            type='email'
                            autoFocus={true}
                            disabled={disabled}
                            placeholder={t('LoginEmailEntry.emailInputPlaceholder')}
                            {...register('email', {
                                required: t('Required.fieldGeneric', { ns: 'shared' }),
                                pattern: {
                                    value: emailRegex,
                                    message: t('LoginEmailEntry.validationError')
                                }
                            })}
                        />
                    </FormField>
                    <PrimaryButton
                        {...autoId(ID_LOGIN_BUTTON)}
                        className={styles.button}
                        disabled={disabled}
                        grow={true}
                        size='large'
                        type='submit'>
                        {t('LoginEmailEntry.submitButton')}
                    </PrimaryButton>
                </form>
            </CardView>
            <div className={styles.bottomContainer}>
                <TertiaryButtonLink {...autoId(ID_CREATE_ACCOUNT_BUTTON)} to={CREATE_ACCOUNT_PATH}>
                    {t('LoginEmailEntry.createAccountLink')}
                </TertiaryButtonLink>
            </div>
        </div>
    );
}
